import { parseSearchParams } from "@/utils/parse";
import { Flex, Group, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import {
  DiscoverFilterCategory,
  DiscoverFilterCountry,
  DiscoverFilterDate,
  DiscoverFilterSize,
} from ".";

export function DiscoverFilters({ label }: { label: string }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialValues = useMemo(
    () => parseSearchParams(searchParams),
    [searchParams]
  );

  function transformValues(
    values: Record<string, any>
  ): Record<string, string> {
    const transformed: Record<string, string> = {};

    // Country Filter: country_id array → country_id__in
    if (
      values.country_id &&
      Array.isArray(values.country_id) &&
      values.country_id.length > 0
    ) {
      transformed["country_id__in"] = values.country_id.join(",");
    }

    // Date Filter: date object → datetime_created__date__gte and datetime_created__date__lte
    if (values.date && typeof values.date === "object") {
      if (values.date.from instanceof Date) {
        transformed["datetime_created__date__gte"] = values.date.from
          .toISOString()
          .split("T")[0];
      }
      if (values.date.to instanceof Date) {
        transformed["datetime_created__date__lte"] = values.date.to
          .toISOString()
          .split("T")[0];
      }
    }

    // Category Filter: category array → category__in
    if (
      values.category &&
      Array.isArray(values.category) &&
      values.category.length > 0
    ) {
      transformed["category__in"] = values.category.join(",");
    }

    // Size Filter: size object → column_count, row_count, byte_count with operators
    if (values.size && typeof values.size === "object") {
      if (typeof values.size.minColumns === "number") {
        transformed["column_count__gte"] = values.size.minColumns.toString();
      }
      if (typeof values.size.maxColumns === "number") {
        transformed["column_count__lte"] = values.size.maxColumns.toString();
      }
      if (typeof values.size.minRows === "number") {
        transformed["row_count__gte"] = values.size.minRows.toString();
      }
      if (typeof values.size.maxRows === "number") {
        transformed["row_count__lte"] = values.size.maxRows.toString();
      }
      if (typeof values.size.minSize === "number") {
        // Convert MB to bytes (round to whole number)
        transformed["byte_count__gte"] = Math.round(
          values.size.minSize * 1024 * 1024
        ).toString();
      }
      if (typeof values.size.maxSize === "number") {
        // Convert MB to bytes (round to whole number)
        transformed["byte_count__lte"] = Math.round(
          values.size.maxSize * 1024 * 1024
        ).toString();
      }
    }

    return transformed;
  }

  const form = useForm({
    initialValues,
    transformValues,
  });

  useEffect(() => {
    form.setValues(initialValues);
  }, [initialValues]);

  const handleSubmit = (values: Record<string, any>) => {
    // Transform values to API format (handles both form submissions and individual filter submissions)
    const transformed = transformValues(values);

    const newParams = new URLSearchParams(searchParams);

    // List of all possible API parameter keys to track for cleanup
    const apiParamKeys = [
      "country_id__in",
      "datetime_created__date__gte",
      "datetime_created__date__lte",
      "category__in",
      "column_count__gte",
      "column_count__lte",
      "row_count__gte",
      "row_count__lte",
      "byte_count__gte",
      "byte_count__lte",
    ];

    // Remove all existing filter parameters first
    apiParamKeys.forEach((key) => {
      newParams.delete(key);
    });

    // Set new transformed parameters
    Object.entries(transformed).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      }
    });

    setSearchParams(newParams, { replace: true });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit as any)}>
      <Flex justify="space-between" w="100%">
        <Flex>
          <DiscoverFilterCountry form={form} onSubmit={handleSubmit as any} />
          <DiscoverFilterDate form={form} onSubmit={handleSubmit as any} />
          <DiscoverFilterCategory form={form} onSubmit={handleSubmit as any} />
          <DiscoverFilterSize form={form} onSubmit={handleSubmit as any} />
        </Flex>
        <Group px="xs">
          <Text fz={15} c="gray.9" fw={400}>
            {label}
          </Text>
        </Group>
      </Flex>
    </form>
  );
}
